import type {Price} from '@/redux/v1/models';

import {
  CashbackInterceptor,
  CashbackInterceptorSize,
  DiscountInterceptor,
  DiscountInterceptorSize,
  Stack,
} from '@travel-frontend/ui-components';
import {useTranslation} from 'react-i18next';

import {HelpTooltip} from '@/components/HelpTooltip';
import {Truncate} from '@/components/Truncate/Truncate';
import {INVIA_TYPE_DISCOUNT} from '@/consts';
import {BestPrice} from '@/modules/common/components/FilterCheckbox/components/BestPrice';
import {styles} from '@/modules/common/components/FilterCheckbox/styles';
import {
  useCombinedCampaign,
  useTourOperatorDiscountCampaign,
} from '@/modules/common/hooks';
import {numberFormat} from '@/utils/numberFormat';

type Props = {
  label: string | JSX.Element;
  optionValue: string;
  description?: string;
  bestPrice?: Price;
  count?: number;
  canShowOptionBestPrice?: boolean;
  canShowOptionCount: boolean;
  canShowOptionCountZero: boolean;
};

export const Label: React.FunctionComponent<Props> = ({
  optionValue,
  canShowOptionCount,
  canShowOptionCountZero,
  bestPrice,
  canShowOptionBestPrice,
  count,
  description,
  label,
}) => {
  const {t} = useTranslation('filters');

  const {shouldShowFilter, filterInterceptorContent} =
    useTourOperatorDiscountCampaign();
  const {shouldShowBanner, shouldShowInterceptor} = useCombinedCampaign();

  const tooltipContent =
    optionValue === 'IT03-ECO#GTI'
      ? t('filters:tooltips.sustainability')
      : description;

  let labelContent = label;
  if (optionValue === INVIA_TYPE_DISCOUNT && shouldShowFilter) {
    labelContent =
      shouldShowBanner || shouldShowInterceptor ? (
        <CashbackInterceptor
          text={filterInterceptorContent.text}
          highlight={filterInterceptorContent.highlight}
          size={CashbackInterceptorSize.INLINE}
        />
      ) : (
        <DiscountInterceptor
          text={filterInterceptorContent.text}
          highlight={filterInterceptorContent.highlight}
          size={DiscountInterceptorSize.INLINE}
        />
      );
  }

  if (
    canShowOptionCount &&
    count !== undefined &&
    (count > 0 || (canShowOptionCountZero && count === 0))
  ) {
    labelContent =
      typeof label === 'string' && count ? (
        `${label} (${numberFormat(count)})`
      ) : (
        <>
          {label}&nbsp;({numberFormat(count)})
        </>
      );
  }

  let content = labelContent;
  if (tooltipContent) {
    content =
      typeof labelContent === 'string' ? (
        <Truncate
          text={labelContent}
          appendix={
            <HelpTooltip
              placement="top"
              content={tooltipContent}
              escapeContent={false}
              css={styles.tooltip}
            />
          }
        />
      ) : (
        <>
          {labelContent}&nbsp;
          <HelpTooltip
            placement="top"
            content={tooltipContent}
            escapeContent={false}
            css={styles.tooltip}
          />
        </>
      );
  }

  const showBestPrice = Boolean(
    canShowOptionBestPrice && bestPrice && bestPrice.amount > 0
  );

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="baseline"
      spacing="xSmall"
    >
      <span>{content}</span>
      {showBestPrice && <BestPrice priceMin={bestPrice} />}
    </Stack>
  );
};
